import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { adminQueries, getErrorMessage, keys } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  toast,
} from '@blimo/ui';

/**
 * G-09: direkt vergebene Rechte (zusätzlich zu den Rollen) – z. B. aus den Altportal-Plugin-Rechten migrierter
 * Mitarbeiter (Rolle „Verwaltung (eingeschränkt)“). Anzeige für `users.view`, Bearbeitung nur mit `canEdit`
 * (UX-Spiegel von UserPolicy::update); vergeben werden können nur Rechte mit `grantable` – das Backend prüft erneut.
 * @param {{ user: { id: number, name: string, permissions?: string[] }, canEdit: boolean }} props
 */
export function DirectPermissionsCard({ user, canEdit }) {
  const api = useApi();
  const catalog = useQuery(adminQueries.permissionCatalog(api));
  const [open, setOpen] = useState(false);
  const labels = useMemo(() => new Map((catalog.data ?? []).map((p) => [p.name, p])), [catalog.data]);
  const permissions = user.permissions ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div className="flex flex-col gap-1">
          <CardTitle as="h2">Direkte Rechte</CardTitle>
          <CardDescription>Zusätzlich zu den Rollen vergeben (z. B. aus Altportal-Plugin-Rechten).</CardDescription>
        </div>
        {canEdit && (
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
            <Pencil aria-hidden="true" /> Rechte bearbeiten
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {permissions.length === 0 ? (
          <p className="text-sm text-muted-foreground">Keine direkten Rechte – es gelten nur die Rollen.</p>
        ) : (
          <ul className="flex flex-wrap gap-1" aria-label="Direkte Rechte">
            {permissions.map((p) => (
              <li key={p}>
                <Badge variant="secondary" title={labels.get(p)?.description || p}>
                  {labels.get(p)?.label ?? p}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      {canEdit && open && <DirectPermissionsSheet user={user} catalog={catalog.data ?? []} open={open} onOpenChange={setOpen} />}
    </Card>
  );
}

function DirectPermissionsSheet({ user, catalog, open, onOpenChange }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState(() => new Set(user.permissions ?? []));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(/** @type {string|null} */ (null));
  const groups = useMemo(() => {
    const map = new Map();
    for (const p of catalog) {
      if (!map.has(p.area_label)) map.set(p.area_label, []);
      map.get(p.area_label).push(p);
    }
    return [...map.entries()];
  }, [catalog]);

  const toggle = (name, checked) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(name);
      else next.delete(name);
      return next;
    });

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const updated = await api.admin.users.update(user.id, { permissions: [...selected] });
      queryClient.setQueryData(keys.admin.detail('users', user.id), updated);
      toast.success('Direkte Rechte gespeichert');
      onOpenChange(false);
    } catch (e) {
      setError(getErrorMessage(e, 'Die Rechte konnten nicht gespeichert werden.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Direkte Rechte</SheetTitle>
          <SheetDescription>{user.name} – Rechte, die Sie selbst nicht besitzen, können nicht vergeben werden.</SheetDescription>
        </SheetHeader>
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
          {groups.map(([area, items]) => (
            <fieldset key={area} className="flex flex-col gap-1.5">
              <legend className="mb-1 text-sm font-medium">{area}</legend>
              {items.map((p) => {
                const checked = selected.has(p.name);
                return (
                  <label key={p.name} className="flex items-start gap-2 text-sm">
                    <Checkbox className="mt-0.5" checked={checked} disabled={!p.grantable && !checked} onChange={(e) => toggle(p.name, e.target.checked)} />
                    <span>
                      {p.label}
                      {!p.grantable && <span className="text-xs text-muted-foreground"> (nicht vergebbar)</span>}
                    </span>
                  </label>
                );
              })}
            </fieldset>
          ))}
          {error && (
            <p role="alert" className="text-sm font-medium text-destructive">
              {error}
            </p>
          )}
        </div>
        <SheetFooter>
          <Button type="button" onClick={save} loading={saving}>
            Speichern
          </Button>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
